import { useEffect, useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import {
  isForwardRunning,
  useForwardStatus,
} from "@/hooks/mutations/use-forward-status";
import queries from "@/queries";
import { resolveFailureStage } from "./constants";

/**
 * 롤포워드 완료 감시 — 매핑 페이지 루트에 1회 마운트한다.
 *
 * 워크북 롤포워드 상태의 **유일한 폴링 옵저버**다(오토태깅의
 * useAutoTagCompletionEffect 와 같은 구조). 진행 모달이 아니라 페이지 루트에 둬야
 * 모달을 닫아도 폴링이 이어져 툴바 버튼의 진행 표시와 완료 처리가 동작한다.
 *
 * 진행 중 → 완료 전환 시 셀·태그 쿼리를 갱신하고 완료 토스트를 띄운다.
 * 이 마운트에서 진행 중(RUNNING)을 직접 목격한 표만 완료로 처리한다 — 안 그러면 이미
 * 끝난 상태로 페이지에 들어올 때마다 완료 토스트가 다시 뜬다.
 */
export const useForwardCompletionEffect = (workbookId: string | undefined) => {
  const queryClient = useQueryClient();
  const { data: status } = useForwardStatus(workbookId, {
    enablePolling: true,
  });

  const doneRef = useRef(false);
  const watchedIdsRef = useRef<Set<string>>(new Set());
  // 직전 폴에서 진행 중이던 표 집합 — 개별 표 완료 감지용.
  const prevRunningIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!status || !workbookId) return;

    const running = status.tables.filter(isForwardRunning);
    // 표 단위 갱신 — 배치의 다른 표가 아직 돌아도, 툴바 스피너가 꺼진 표는 태그가 바로
    // 반영돼야 한다(오토태깅 완료 감시와 같은 방식).
    const runningIds = new Set(running.map((t) => t.tableId));
    prevRunningIdsRef.current.forEach((tableId) => {
      if (runningIds.has(tableId)) return;
      queryClient.invalidateQueries({
        queryKey: queries.workbook(workbookId)._ctx.tables(tableId).queryKey,
      });
    });
    prevRunningIdsRef.current = runningIds;

    if (running.length > 0) {
      doneRef.current = false;
      running.forEach((t) => watchedIdsRef.current.add(t.tableId));
      return;
    }

    if (doneRef.current) return;
    doneRef.current = true;

    // 이번 마운트에서 실제로 실행 중을 본 표만 완료 알림 대상. 없으면 조용히 지나간다.
    const watched = status.tables.filter((t) =>
      watchedIdsRef.current.has(t.tableId),
    );
    watchedIdsRef.current = new Set();
    if (watched.length === 0) return;

    queryClient.invalidateQueries({
      queryKey: queries.workbook(workbookId).queryKey,
    });
    // 이월 실패와 체인 오토태깅 실패는 조치가 다르다 — 문구를 나눠 안내한다.
    const forwardFailed = watched.filter(
      (t) => resolveFailureStage(t) === "FORWARD",
    ).length;
    const autoFailed = watched.filter(
      (t) => resolveFailureStage(t) === "AUTO_TAG",
    ).length;
    if (forwardFailed > 0 && autoFailed > 0) {
      toast.warning(
        `표 ${forwardFailed}개의 불러오기가 실패하고, 표 ${autoFailed}개는 이월 후 오토태깅이 실패했습니다.`,
      );
    } else if (forwardFailed > 0) {
      toast.warning(
        `불러오기가 끝났지만 표 ${forwardFailed}개가 실패했습니다.`,
      );
    } else if (autoFailed > 0) {
      toast.warning(
        `태그 이월은 끝났지만 표 ${autoFailed}개의 오토태깅이 실패했습니다. 해당 표는 직접 태깅해 주세요.`,
      );
    } else {
      toast.success("새 워크북의 태그를 불러왔습니다.");
    }
  }, [status, workbookId, queryClient]);
};
